import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { FiSearch, FiX } from "react-icons/fi";
import "./Sidebar.css";

import {
  updateCurrentBlockState,
  moveBlock,
} from "../../../Redux/actions/projectActions";
import { useDispatch } from "react-redux";
import {
  blockMatchesQuery,
  getFirstMatchingItemText,
  highlightSearchText,
  normalizeSearchText,
} from "../creditSearch";

const scrollTo = (parent, child) => {
  let parent1 = document.getElementById(parent);
  let child1 = document.getElementById(child);
  if (child1 && parent1) {
    parent1?.scrollTo({
      behavior: "smooth",
      top:
        child1.offsetTop -
        document.getElementById("navbar_1").offsetHeight -
        150,
    });
  }
};

const SidebarDD = ({
  currentBlockId,
  blocks,
  projectData,
  searchQuery = "",
  setSearchQuery,
}) => {
  const dispatch = useDispatch();
  const [draggedBlockId, setDraggedBlockId] = useState(null);
  const [localSearch, setLocalSearch] = useState("");
  const searchValue = setSearchQuery ? searchQuery : localSearch;
  const onSearchChange = setSearchQuery || setLocalSearch;
  const [tooltip, setTooltip] = useState(null);
  const currentBlockIdx = projectData?.currentBlock?.idx;

  const setSelectedBlock = (idx, blockId) => {
    dispatch(updateCurrentBlockState(blockId, idx));
  };

  const query = normalizeSearchText(searchValue);
  const isSearching = query.length > 0;
  const allBlocks = useMemo(
    () => (blocks || []).map((block, idx) => ({ ...block, idx })),
    [blocks],
  );
  const matchingBlocks = useMemo(() => {
    if (!query) return [];
    const credits = projectData?.credits || [];
    return allBlocks.filter((block) => {
      const credit =
        credits[block.idx]?._id === block._id
          ? credits[block.idx]
          : credits.find((c) => c._id === block._id);
      return blockMatchesQuery(block, credit, query);
    });
  }, [allBlocks, projectData?.credits, query]);

  const visibleBlocks = isSearching ? matchingBlocks : allBlocks;
  const currentBlockIdRef = useRef(currentBlockId);
  currentBlockIdRef.current = currentBlockId;

  useEffect(() => {
    if (!query || matchingBlocks.length === 0) return;
    if (
      matchingBlocks.some((block) => block._id === currentBlockIdRef.current)
    ) {
      return;
    }
    const first = matchingBlocks[0];
    dispatch(updateCurrentBlockState(first._id, first.idx));
  }, [query, matchingBlocks, dispatch]);

  useEffect(() => {
    try {
      scrollTo("menu-item-paent", "content" + currentBlockIdx);
    } catch (error) {
      console.log(error);
    }
  }, [currentBlockIdx]);

  const handleDragStart = (event, blockId) => {
    setDraggedBlockId(blockId);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", blockId);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (event, blockId, targetIdx) => {
    event.preventDefault();
    const sourceIdx = blocks.findIndex((block) => block._id === draggedBlockId);
    if (sourceIdx !== targetIdx) {
      dispatch(moveBlock(sourceIdx, targetIdx));
      dispatch(updateCurrentBlockState(blockId, targetIdx));
    }
    setDraggedBlockId(null);
  };

  const cycleMatch = (delta) => {
    if (!matchingBlocks.length) return;
    const currentIndex = matchingBlocks.findIndex(
      (block) => block._id === currentBlockId,
    );
    const nextIndex =
      currentIndex === -1
        ? 0
        : (currentIndex + delta + matchingBlocks.length) %
          matchingBlocks.length;
    const next = matchingBlocks[nextIndex];
    setSelectedBlock(next.idx, next._id);
  };

  return (
    <div className="Sidebar" id="content">
      <div className="w-full xl:h-[60px] 2xl:h-[48px] flex items-center px-3 border-b border-[rgba(255,255,255,.06)] flex-shrink-0">
        <div className=" inline-block group   cursor-help underline decoration-dashed decoration-[rgba(255,255,255,0.4)] underline-offset-4">
          <span className="cursor-help uppercase xl:text-[14px] 2xl:text-[11.5px] mb-[8px] tracking-[.13em] font-medium font-mono text-white ">
            Credit Blocks
          </span>
          <span className="absolute hidden group-hover:block cursor-help ml-[120px] mt-[-30px] font-body p-2 rounded-xl bg-[#191824] text-[#eee9ff] border-[1px] border-[rgba(255,255,255,0.1)] text-xs shadow-xl w-[200px] max-w-[200px] text-left z-[10] ">
            Manage and navigate through the individual credit sections in your
            project.
          </span>
        </div>
      </div>

      <div className="sidebarSearchWrap">
        <div className="sidebarSearchField">
          <FiSearch className="sidebarSearchIcon" aria-hidden="true" />
          <input
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") onSearchChange("");
              if (e.key === "Enter" && matchingBlocks.length) {
                e.preventDefault();
                cycleMatch(e.shiftKey ? -1 : 1);
              }
            }}
            placeholder="Search names, roles..."
            aria-label="Search credits in the preview"
            autoComplete="off"
            spellCheck={false}
            className={`sidebarSearchInput${isSearching ? " sidebarSearchInput--active" : ""}`}
          />
          {isSearching && (
            <>
              <span className="sidebarSearchCount">
                {matchingBlocks.length}
              </span>
              <button
                type="button"
                onClick={() => onSearchChange("")}
                aria-label="Clear search"
                className="sidebarSearchClear"
              >
                <FiX className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
        {isSearching && (
          <p className="sidebarSearchMeta">
            {matchingBlocks.length === 0
              ? "No matches"
              : `${matchingBlocks.length} block${matchingBlocks.length === 1 ? "" : "s"} · Enter to cycle`}
          </p>
        )}
      </div>

      <div className="menu-item " id="menu-item-paent">
        {isSearching && visibleBlocks.length === 0 && (
          <div className="sidebar-search-empty">No blocks match</div>
        )}

        {visibleBlocks.map((block) => {
          const idx = block.idx;
          const isActive = currentBlockId === block._id;
          const credit =
            projectData?.credits?.[idx]?._id === block._id
              ? projectData.credits[idx]
              : projectData?.credits?.find((c) => c._id === block._id);
          const matchedItemText = getFirstMatchingItemText(block, credit, query);
          const itemClass = [
            isActive ? "menuItemActive" : "menuItem",
            "font-mono inline-block",
            isSearching ? "menuItemMatch" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <div
              className={itemClass}
              id={"content" + idx}
              key={block._id}
              onClick={() => setSelectedBlock(idx, block?._id)}
              draggable={!isSearching}
              onDragStart={(event) => handleDragStart(event, block?._id)}
              onDragOver={handleDragOver}
              onDrop={(event) => handleDrop(event, block._id, idx)}
              onMouseEnter={(event) => {
                const r = event.currentTarget.getBoundingClientRect();
                setTooltip({
                  text: block?.block_type,
                  top: r.top + r.height / 2,
                  left: r.right + 8,
                });
              }}
              onMouseLeave={() => setTooltip(null)}
            >
              <span className="sidebarBlockLabel">
                {isSearching
                  ? highlightSearchText(block?.block_name, query)
                  : block?.block_name}
              </span>
              {matchedItemText && (
                <span className="sidebarMatchHint">
                  {highlightSearchText(matchedItemText, query)}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="h-[6%] xl:h-[6%] 2xl:h-[6%] px-4 w-full border-t-[1px] border-[#ffffff0e] flex items-center justify-start flex-shrink-0 ">
        <a
          href="/help/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[13px] font-medium text-[#a8a4c8] hover:text-[#c4b2ff] underline underline-offset-4 decoration-[rgba(255,255,255,0.4)] hover:decoration-[#c4b2ff] transition-colors cursor-pointer"
        >
          Need Help?
        </a>
      </div>

      {tooltip &&
        createPortal(
          <div
            className="font-body font-medium px-2 py-1.5 rounded-lg bg-[#191824] capitalize text-[#eee9ff] border-[1px] border-[rgba(255,255,255,0.1)] text-xs shadow-xl max-w-[200px] text-left pointer-events-none"
            style={{
              position: "fixed",
              top: tooltip.top,
              left: tooltip.left,
              transform: "translateY(-50%)",
              zIndex: 9999,
            }}
          >
            {tooltip.text}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default SidebarDD;
